import { IList, IField } from 'app/services/api5-service/api.interface';
import { combineLatest, merge, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { filterNotDefinedItems } from 'utils/filter-not-defined-items';

import { getListCardsData } from './list-cards/utils/get-list-cards-data';
import { getListTableData } from './list-table/utils/get-list-table-data';
import { IListRowUi, ListConfig, ListProps } from './model';
import {
  getListType,
  getListRowsUi,
  getListHeaderCells,
  separateListColumns,
} from './utils';
import { isListHasStub } from './utils/get-list-stub';

import { ISPFieldConfig, ISPFieldType, ISPFormState } from '../../model';

/**
 * Check is list should be hidden
 *
 * @param control
 * @param field
 * @param state
 */
function isListShouldBeHidden(
  control: IList,
  field: IField,
  state: ISPFormState,
): boolean {
  const elements = state.listService.getListElems(control.$name) || [];
  const isEmptyList = !elements.length;

  const hasStub = isListHasStub(control, state);

  return (
    state.hiddenService.isHidden(control.$name, field.$name) ||
    (isEmptyList && !hasStub)
  );
}

/**
 * Stream of list hidden state
 *
 * @param control
 * @param field
 * @param state
 */
function isListShouldBeHidden$(
  control: IList,
  field: IField,
  state: ISPFormState,
): Observable<boolean> {
  const isNeedHideEmptyList$ = merge(
    state.hiddenService.isHidden$(field.$name),
    state.listService.getListElements$(control.$name),
    state.listService.isListWithFilter$(control.$name),
  ).pipe(map(() => isListShouldBeHidden(control, field, state)));

  return combineLatest([
    state.hiddenService.isHidden$(field.$name),
    isNeedHideEmptyList$,
  ]).pipe(map(states => states.some(isHidden => isHidden)));
}

/**
 * Update config fieldGroup array of child configs
 *
 * @param config - list config
 * @param rows - list rows with child configs
 */
function updateFieldGroup(config: ListConfig, rows: IListRowUi[]): void {
  const nextConfigs = filterNotDefinedItems(
    rows.map(n => n.cells.map(c => c.config)).flat(),
  );

  config.fieldGroup = nextConfigs;
}

/**
 * Get props for list field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 * @param config - list config
 */
export function getListProps(
  control: IList,
  field: IField,
  state: ISPFormState,
  config: ListConfig,
): ListProps {
  const type = getListType(control);

  const rows$ = state.listService.getListElements$(control.$name).pipe(
    map(elems => getListRowsUi(control, field, elems, state, config)),
    tap(rows => updateFieldGroup(config, rows)),
    tap(rows => {
      const buttons = filterNotDefinedItems(
        rows.flatMap(row => row.cells.flatMap(cell => cell.buttons)),
      );
      state.buttonsService.setListButtons(control.$name, buttons);
    }),
    switchMap(rows => state.listService.getFilteredRows$(control.$name, rows)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  return {
    originalControl: control,
    originalField: field,
    displayCountInHeaderSubtitle:
      state.docSubject.value.metadata?.form?.$display_count === control.$name,
    isHidden: isListShouldBeHidden(control, field, state),
    isHidden$: isListShouldBeHidden$(control, field, state),
    type,
    doNotWrappIntoInlineGroup: true,
    style: {
      minWidth: 'min-content',
    },
    data$: rows$.pipe(
      map(rows => {
        const customHeaders = state.listService.getListCustomHeaders(
          control.$name,
        );
        const { simpleColumns } = separateListColumns(control.col);
        const headerCells = getListHeaderCells(
          state.docSubject.value,
          simpleColumns,
          customHeaders,
        );
        if (type === 'block') {
          return getListCardsData(control, rows, state, headerCells);
        } else {
          const elements = state.listService.getListElems(control.$name) || [];
          return getListTableData(
            elements,
            rows,
            field,
            state,
            control,
            headerCells,
          );
        }
      }),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      }),
    ),
  };
}

/**
 * Get config for list field
 *
 * @param control - control (subfield) metadata
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function getListConfig(
  control: IList,
  field: IField,
  state: ISPFormState,
): ListConfig {
  const config = {
    type: ISPFieldType.List,
    fieldGroup: [] as ISPFieldConfig[],
  } as ListConfig;

  const props = getListProps(control, field, state, config);

  config.props = props;

  return config;
}
